import type { ReactElement, RefObject } from 'react'
import type { TFunction } from 'i18next'
import { Briefcase, Clock, Database, Edit, MoreVertical, Trash2, TrendingUp } from 'lucide-react'
import type { Case } from '../../../types/case-management'
import { STATUS_ICONS, formatRelativeTime, type PriorityStyle } from './case-card.helpers'

interface BadgesProps {
  caseData: Case
  priority: PriorityStyle
  dark: boolean
  t: TFunction<'cases'>
}

export function Badges({ caseData, priority, dark, t }: BadgesProps): ReactElement {
  return (
    <div className="flex items-center gap-2">
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium"
        style={{
          background: dark ? priority.bgDark : priority.bgLight,
          color: dark ? priority.textDark : priority.textLight,
        }}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${priority.dot}`} />
        {t(`priority.${caseData.priority}`)}
      </span>
      <span
        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
        style={{ background: 'var(--highlight)', color: 'var(--text-dim)' }}
      >
        <span className="text-[10px]">{STATUS_ICONS[caseData.status]}</span>
        {t(`caseStatus.${caseData.status}`)}
      </span>
    </div>
  )
}

interface MenuProps {
  menuRef: RefObject<HTMLDivElement>
  menuOpen: boolean
  onToggle: (event: React.MouseEvent) => void
  onEdit: (event: React.MouseEvent) => void
  onCloseMenu: () => void
  t: TFunction<'cases'>
}

export function Menu({
  menuRef,
  menuOpen,
  onToggle,
  onEdit,
  onCloseMenu,
  t,
}: MenuProps): ReactElement {
  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={onToggle}
        className="rounded p-1 transition-colors"
        style={{ color: 'var(--text-muted)' }}
        title={t('card.caseActions')}
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {menuOpen && <MenuPanel onEdit={onEdit} onCloseMenu={onCloseMenu} t={t} />}
    </div>
  )
}

interface MenuPanelProps {
  onEdit: (event: React.MouseEvent) => void
  onCloseMenu: () => void
  t: TFunction<'cases'>
}

function MenuPanel({ onEdit, onCloseMenu, t }: MenuPanelProps): ReactElement {
  return (
    <div
      className="absolute top-full right-0 z-50 mt-1 w-36 rounded-lg border py-1 shadow-lg"
      style={{ background: 'var(--bg-card)', borderColor: 'var(--border)' }}
    >
      <button
        onClick={onEdit}
        className="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors"
        style={{ color: 'var(--text-dim)' }}
        onMouseEnter={(event) => {
          event.currentTarget.style.background = 'var(--hover)'
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.background = 'transparent'
        }}
      >
        <Edit className="h-4 w-4" />
        {t('card.editCase')}
      </button>
      <button
        onClick={(event) => {
          event.stopPropagation()
          onCloseMenu()
        }}
        className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 transition-colors hover:bg-red-50"
      >
        <Trash2 className="h-4 w-4" />
        {t('card.deleteCase')}
      </button>
    </div>
  )
}

interface TopRowProps {
  caseData: Case
  priority: PriorityStyle
  dark: boolean
  menuRef: RefObject<HTMLDivElement>
  menuOpen: boolean
  onMenuToggle: (event: React.MouseEvent) => void
  onEdit: (event: React.MouseEvent) => void
  onCloseMenu: () => void
  t: TFunction<'cases'>
}

export function TopRow(props: TopRowProps): ReactElement {
  const { caseData, priority, dark, menuRef, menuOpen, onMenuToggle, onEdit, onCloseMenu, t } =
    props
  return (
    <div className="mb-3 flex items-center justify-between pt-1">
      <Badges caseData={caseData} priority={priority} dark={dark} t={t} />
      <div className="flex items-center gap-2">
        <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-muted)' }}>
          <Clock className="h-3 w-3" />
          {formatRelativeTime(caseData.lastActivityAt, t)}
        </span>
        <Menu
          menuRef={menuRef}
          menuOpen={menuOpen}
          onToggle={onMenuToggle}
          onEdit={onEdit}
          onCloseMenu={onCloseMenu}
          t={t}
        />
      </div>
    </div>
  )
}

interface ClientLineProps {
  clientDisplay: string
  orgCaseReference?: string | undefined
  t: TFunction<'cases'>
}

export function ClientLine({ clientDisplay, orgCaseReference, t }: ClientLineProps): ReactElement {
  return (
    <>
      <p className="mb-4 flex items-center gap-1.5 text-sm" style={{ color: 'var(--text-dim)' }}>
        <Briefcase className="h-3.5 w-3.5" style={{ color: 'var(--text-muted)' }} />
        {clientDisplay}
      </p>
      {orgCaseReference && (
        <p className="mb-3 font-mono text-xs" style={{ color: 'var(--text-muted)' }}>
          {t('card.ref')} {orgCaseReference}
        </p>
      )}
    </>
  )
}

interface CountsProps {
  collectionCount: number
  artifactCount: number
  t: TFunction<'cases'>
}

export function Counts({ collectionCount, artifactCount, t }: CountsProps): ReactElement {
  return (
    <div className="mb-3 flex items-center gap-4 text-xs" style={{ color: 'var(--text-muted)' }}>
      <span className="flex items-center gap-1">
        <Database className="h-3.5 w-3.5" />
        {t('card.collections', { count: collectionCount })}
      </span>
      <span className="flex items-center gap-1">
        <TrendingUp className="h-3.5 w-3.5" />
        {t('card.artifacts', { count: artifactCount })}
      </span>
    </div>
  )
}

interface ProgressProps {
  completionPercent: number
  t: TFunction<'cases'>
}

export function Progress({ completionPercent, t }: ProgressProps): ReactElement {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span style={{ color: 'var(--text-muted)' }}>{t('card.analysis')}</span>
        <span className="font-medium" style={{ color: 'var(--text-dim)' }}>
          {completionPercent}%
        </span>
      </div>
      <div
        className="h-1.5 overflow-hidden rounded-full"
        style={{ background: 'var(--highlight)' }}
      >
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{
            width: `${completionPercent.toString()}%`,
            background:
              completionPercent === 100
                ? 'linear-gradient(90deg, #22c55e, #16a34a)'
                : 'linear-gradient(90deg, #84cc16, #65a30d)',
          }}
        />
      </div>
    </div>
  )
}

interface TitleButtonProps {
  name: string
  onNavigate: () => void
}

export function TitleButton({ name, onNavigate }: TitleButtonProps): ReactElement {
  return (
    <button onClick={onNavigate} className="w-full text-left">
      <h3
        className="mb-1 line-clamp-2 cursor-pointer font-semibold transition-colors"
        style={{ color: 'var(--text)', fontFamily: 'var(--font-display, Syne, sans-serif)' }}
        onMouseEnter={(event) => {
          event.currentTarget.style.color = 'rgb(101, 163, 13)'
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.color = 'var(--text)'
        }}
      >
        {name}
      </h3>
    </button>
  )
}

export { PRIORITY_STYLES } from './case-card.helpers'
